import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './videos.scss';

const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_API_URL_PRODUCTION
    : process.env.REACT_APP_API_URL;

const videoExtensionsRegex = /\.(mp4|mov|avi|webm|mkv)$/i;

const Videos = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [modalVisible, setModalVisible] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState([]);
  const [categorySearch, setCategorySearch] = useState('');

  const [form, setForm] = useState({
    name: '',
    description: '',
    file: null,
  });

  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    fetchVideos();
    fetchCategories();
  }, []);

  const fetchVideos = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/videos`);
      const filtered = res.data.filter(video => videoExtensionsRegex.test(video.url));
      setVideos(filtered);
    } catch (err) {
      console.error('Error fetching videos:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/image-categories`);
      setCategories(res.data);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'file') {
      setForm({ ...form, file: files[0] });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const resetForm = () => {
    setForm({ name: '', description: '', file: null });
    setSelectedCategories([]);
    setTags([]);
    setTagInput('');
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setModalVisible(true);
    setProgress(0);

    try {
      if (editingId) {
        await axios.patch(`${API_BASE_URL}/api/videos/${editingId}`, {
          name: form.name,
          description: form.description,
          tags,
          imageCategories: selectedCategories,
        });
      } else {
        const formData = new FormData();
        formData.append('name', form.name);
        formData.append('description', form.description);
        formData.append('tags', JSON.stringify(tags));
        formData.append('imageCategories', JSON.stringify(selectedCategories));
        if (form.file) formData.append('file', form.file);

        await axios.post(`${API_BASE_URL}/api/videos/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setProgress(percent);
          },
        });
      }

      await fetchVideos();
      toast.success(editingId ? 'Video updated' : 'Video uploaded');
      resetForm();
    } catch (err) {
      console.error('Submit failed:', err);
      toast.error('Something went wrong');
    } finally {
      setModalVisible(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this video?')) return;
    setModalVisible(true);

    try {
      await axios.delete(`${API_BASE_URL}/api/videos/${id}`);
      setVideos(videos.filter(v => v._id !== id));
      toast.success('Video deleted');
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Failed to delete');
    } finally {
      setModalVisible(false);
    }
  };

  const startEdit = (video) => {
    setEditingId(video._id);
    setForm({
      name: video.name,
      description: video.description,
      file: null,
    });
    setSelectedCategories(video.imageCategories.map(cat => cat._id));
    setTags(video.tags);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleCategory = (id) => {
    setSelectedCategories(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  const addTag = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const newTag = tagInput.trim();
      if (newTag && !tags.includes(newTag)) {
        setTags([...tags, newTag]);
      }
      setTagInput('');
    }
  };

  const removeTag = (t) => {
    setTags(tags.filter(tag => tag !== t));
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.info('Video link copied!');
  };

  return (
    <div className="videos-wrapper">
      <form className="video-form" onSubmit={handleSubmit}>
        <h2>{editingId ? 'Edit Video' : 'Upload Video'}</h2>

        <input
          type="text"
          name="name"
          placeholder="Video name"
          value={form.name}
          onChange={handleChange}
          required
        />
        <textarea
          name="description"
          placeholder="Description"
          value={form.description}
          onChange={handleChange}
        />

        <div className="chips-input">
          {tags.map(tag => (
            <div className="chip" key={tag}>
              {tag} <span className="remove" onClick={() => removeTag(tag)}>×</span>
            </div>
          ))}
          <input
            type="text"
            value={tagInput}
            onChange={e => setTagInput(e.target.value)}
            onKeyDown={addTag}
            placeholder="Add tag..."
          />
        </div>

        <input
          type="text"
          className="category-search-input"
          placeholder="Search categories..."
          value={categorySearch}
          onChange={(e) => setCategorySearch(e.target.value)}
        />

        <div className="category-scroll">
          {categories
            .filter(cat => cat.name.toLowerCase().includes(categorySearch.toLowerCase()))
            .map(cat => (
              <div
                key={cat._id}
                className={`category-option ${selectedCategories.includes(cat._id) ? 'selected' : ''}`}
                onClick={() => toggleCategory(cat._id)}
              >
                {cat.name}
              </div>
            ))}
        </div>

        {!editingId && (
          <input type="file" name="file" accept="video/*" onChange={handleChange} required />
        )}

        {progress > 0 && progress < 100 && (
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%` }}></div>
          </div>
        )}

        <button type="submit">
          {editingId ? 'Update Video' : 'Upload Video'}
        </button>
        {editingId && (
          <button type="button" onClick={resetForm} className="cancel-btn">
            Cancel
          </button>
        )}
      </form>

      {modalVisible && (
        <div className="modal-overlay">
          <div className="modal-spinner">Please wait...</div>
        </div>
      )}

      {loading ? (
        <div className="videos-loading">Loading videos...</div>
      ) : videos.length === 0 ? (
        <div className="videos-empty">No videos uploaded yet.</div>
      ) : (
        <div className="video-list">
          {videos.map((video) => (
            <div className="video-card" key={video._id}>
              <video controls width="100%">
                <source src={video.url} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <div className="video-info">
                <h3>{video.name}</h3>
                <p>{video.description}</p>
                <div className="video-tags">
                  {video.tags.map((tag, idx) => (
                    <span key={idx} className="video-tag">#{tag}</span>
                  ))}
                </div>
                <div className="video-categories">
                  Categories:
                  {video.imageCategories.map(cat => (
                    <span key={cat._id} className="video-category">{cat.name}</span>
                  ))}
                </div>
                <div className="video-actions">
                  <button onClick={() => startEdit(video)}>Edit</button>
                  <button onClick={() => handleDelete(video._id)}>Delete</button>
                  <button onClick={() => copyLink(video.url)}>Copy Link</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Videos;
